import { z } from 'zod';
import { supabase } from '@/shared/supabase/client';
import type { DepotPartenaires } from '../types';
import { cheminConformite } from './chemins';
import {
  COLONNES_PARTENAIRE,
  SELECTION_FICHE_PARTENAIRE,
  SELECTION_RESUME_PARTENAIRE,
  ligneDeSaisieContactPartenaire,
  ligneDeSaisiePartenaire,
  ligneFichePartenaire,
  lignePartenaire,
  ligneResumePartenaire,
  messageDeReponse,
  messageErreur,
  versFichePartenaire,
  versPartenaire,
  versResumePartenaire,
} from './lignes';
import { adresseSignee, deposerPuisEnregistrer, retirerFichiers } from './stockage';
import { COMPARTIMENT_PHOTOS } from '@/shared/comptes/photo';
import { compresserPhoto } from '@/shared/hors-ligne/compression';

const lignesId = z.array(z.object({ id: z.string() }));
const lignesChemin = z.array(z.object({ chemin: z.string() }));

export const partenairesSupabase: DepotPartenaires = {
  async lister() {
    const { data, error } = await supabase()
      .from('partenaires')
      .select(SELECTION_RESUME_PARTENAIRE)
      .order('raison_sociale');
    if (error) throw new Error(messageErreur(error, 'Les partenaires n’ont pas pu être lus.'));
    return z.array(ligneResumePartenaire).parse(data).map(versResumePartenaire);
  },

  async lire(id) {
    const { data, error } = await supabase()
      .from('partenaires')
      .select(SELECTION_FICHE_PARTENAIRE)
      .eq('id', id)
      .maybeSingle();
    if (error) throw new Error(messageErreur(error, 'La fiche partenaire n’a pas pu être lue.'));
    if (data === null) return null;
    return versFichePartenaire(ligneFichePartenaire.parse(data));
  },

  async creer(saisie) {
    const { data, error } = await supabase()
      .from('partenaires')
      .insert(ligneDeSaisiePartenaire(saisie))
      .select(COLONNES_PARTENAIRE)
      .single();
    if (error) throw new Error(messageErreur(error, 'Le partenaire n’a pas pu être créé.'));
    return versPartenaire(lignePartenaire.parse(data));
  },

  async modifier(id, saisie) {
    const { data, error } = await supabase()
      .from('partenaires')
      .update(ligneDeSaisiePartenaire(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageErreur(error, 'Le partenaire n’a pas pu être modifié.'));
    if (lignesId.parse(data).length === 0) throw new Error('Ce partenaire n’existe plus.');
  },

  async supprimer(id) {
    /* Le partenaire, ses comptes et ses documents (db/024) ; ses étapes restent
       aux chantiers, sans lui. La base rend les fichiers à effacer du stockage. */
    const reponse = await supabase().rpc('supprimer_partenaire_complet', {
      p_partenaire: id,
    });
    if (reponse.error)
      throw new Error(messageErreur(reponse.error, 'Le partenaire n’a pas pu être supprimé.'));
    const chemins: unknown = reponse.data;
    await retirerFichiers(z.array(z.string()).parse(chemins ?? []));
  },

  async ajouterConformite(partenaireId, fichier, type, expireLe) {
    const chemin = cheminConformite(partenaireId, fichier.name, crypto.randomUUID());
    await deposerPuisEnregistrer(chemin, fichier, () =>
      supabase().from('documents_conformite').insert({
        partenaire_id: partenaireId,
        type,
        expire_le: expireLe,
        nom: fichier.name,
        taille: fichier.size,
        type_mime: fichier.type,
        chemin,
      })
    );
  },

  async supprimerConformite(id) {
    const { data, error } = await supabase()
      .from('documents_conformite')
      .delete()
      .eq('id', id)
      .select('chemin');
    if (error) throw new Error(messageErreur(error, 'Le document n’a pas pu être supprimé.'));
    await retirerFichiers(lignesChemin.parse(data).map((d) => d.chemin));
  },

  adresseDocument(chemin) {
    return adresseSignee(chemin);
  },

  async ajouterContact(partenaireId, saisie) {
    const { error } = await supabase()
      .from('contacts_partenaires')
      .insert({ partenaire_id: partenaireId, ...ligneDeSaisieContactPartenaire(saisie) });
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être ajouté.'));
  },

  async modifierContact(id, saisie) {
    const { data, error } = await supabase()
      .from('contacts_partenaires')
      .update(ligneDeSaisieContactPartenaire(saisie))
      .eq('id', id)
      .select('id');
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être modifié.'));
    if (lignesId.parse(data).length === 0) throw new Error('Ce contact n’existe plus.');
  },

  async supprimerContact(id) {
    const { error } = await supabase().from('contacts_partenaires').delete().eq('id', id);
    if (error) throw new Error(messageErreur(error, 'Le contact n’a pas pu être retiré.'));
  },

  async changerLogo(partenaireId, fichier) {
    const logos = supabase().storage.from(COMPARTIMENT_PHOTOS);
    let chemin: string | null = null;
    if (fichier) {
      /* Un PNG garde sa transparence s'il est léger ; sinon on le réduit. */
      const leger = fichier.size <= 1024 * 1024 && /^image\/(png|jpeg|webp)$/.test(fichier.type);
      const image = leger ? fichier : await compresserPhoto(fichier, 512);
      if (image.size > 2 * 1024 * 1024) throw new Error('Le logo dépasse 2 Mo.');
      const extension =
        image.type === 'image/png' ? 'png' : image.type === 'image/webp' ? 'webp' : 'jpg';
      chemin = `partenaires/${partenaireId}/${String(Date.now())}.${extension}`;
      const depot = await logos.upload(chemin, image, {
        contentType: image.type || 'image/jpeg',
        upsert: false,
      });
      if (depot.error) throw new Error('Le logo n’a pas pu être envoyé.');
    }
    const reponse = await supabase().rpc('definir_logo_partenaire', {
      p_partenaire: partenaireId,
      p_chemin: chemin,
    });
    if (reponse.error) {
      if (chemin) await logos.remove([chemin]);
      throw new Error(messageErreur(reponse.error, 'Le logo n’a pas pu être enregistré.'));
    }
    const ancien: unknown = reponse.data;
    /* L'ancien ne sert plus ; un échec ici laisse un fichier orphelin, sans plus. */
    if (typeof ancien === 'string' && ancien !== chemin) await logos.remove([ancien]);
  },

  async inviter(partenaireId, invitation) {
    /* La création d'un compte demande la clé de service : elle ne quitte jamais
       le serveur (functions/api/inviter.ts). On y présente la session du bureau. */
    const { data } = await supabase().auth.getSession();
    const jeton = data.session?.access_token;
    if (!jeton) throw new Error('Votre session a expiré : reconnectez-vous.');

    let reponse: Response;
    try {
      reponse = await fetch('/api/inviter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jeton}` },
        body: JSON.stringify({
          partenaireId,
          email: invitation.email.trim(),
          nom: invitation.nom.trim(),
        }),
      });
    } catch {
      throw new Error('Le service d’invitation est injoignable. Vérifiez la connexion.');
    }
    if (reponse.ok) return;

    let corps: unknown = null;
    try {
      corps = await reponse.json();
    } catch {
      /* Pas de JSON (page d'erreur du proxy, par exemple) : message par défaut. */
    }
    throw new Error(messageDeReponse(corps) ?? 'L’invitation n’a pas pu être envoyée.');
  },
};
